import type { AdaptiveSubmit, JsonObject } from 'components/AdaptiveCard/AdaptiveCardRenderer';
import { questionsCardAction } from 'components/Copilot/AutomationDrafting/automationDraftingAction';
import {
  ContentBlockKind,
  CopilotConversation,
  CopilotConversationContentBlock,
} from 'models/copilot/copilotConversation';
import {
  describeQuestionAnswers,
  parseQuestionAnswersJson,
  questionAnswersInput,
  questionSetIdInput,
} from 'models/copilot/questionSet';

/**
 * The question card the general knowledge loop presents (present kind QuestionSet) is answered by the shell
 * when no running process took it: the answers become the next chat turn. A running process (the
 * automation drafting) answers its own question card first, through the active usage.
 */
export const isQuestionSetCardSubmit = (
  block: CopilotConversationContentBlock,
  payload: AdaptiveSubmit<JsonObject>,
): boolean => block.kind === ContentBlockKind.QuestionSet && payload.actionData.action === questionsCardAction;

// The wording of the answered questions, the text the model reads; undefined when nothing was answered
export const getQuestionSetAnswerText = (inputs: Record<string, string>): string | undefined => {
  const answers = parseQuestionAnswersJson(inputs[questionAnswersInput]);
  const text = answers ? describeQuestionAnswers(answers) : '';

  return text.trim() === '' ? undefined : text;
};

// The setId of the answered card: the request carries it, so the server releases the session waiting on the card
export const getQuestionSetId = (inputs: Record<string, string>): string | undefined =>
  inputs[questionSetIdInput] || undefined;

// A copy of the conversation whose card holds the sent answers: the card then shows them read-only
export const withQuestionSetAnswers = (
  conversation: CopilotConversation,
  itemId: string | undefined,
  blockId: string | undefined,
  inputs: Record<string, string>,
): CopilotConversation => {
  const next = conversation.clone();
  const block = next.thread
    .find((item) => item.id === itemId)
    ?.contentBlocks?.find((contentBlock) => contentBlock.id === blockId);
  if (block) block.adaptiveInputs = { ...(block.adaptiveInputs ?? {}), ...inputs };

  return next;
};
